import { Button } from '@/components/base/button'
import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
import { Divider } from '@/components/base/divider'
import { useEffect, useState } from 'react'
import { WarehouseQuery } from './__generated__/show_query'
import { Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Accordion } from '@/components/accordion'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Query as TableQuery } from './table_query'
import { EditDialog } from './edit_dialog'
import { Table as AssetTable } from '@/components/asset/table'
import { MapPinIcon } from '@heroicons/react/24/solid'
import { Subheading } from '../base/heading'

type Warehouse = WarehouseQuery['warehouses']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null)

  const { data, loading, error } = useQuery<WarehouseQuery>(Query, { variables: { filters: { idEq: id } } })

  useEffect(() => {
    if (!data || !id) { return }

    setWarehouse(data.warehouses.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }
  const onDelete = () => {
    setOpenDelete(true)
  }

  const onDeleteSuccess = () => {
    setOpenDelete(false)
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={() => setOpen(false)}>
        <DialogTitle>
          { loading || !warehouse
            ? <Loading />
            : <div className="flex flex-row justify-between">
              <div>
                <span className="flex flex-row gap-4 items-center pb-6">
                  { warehouse.name }
                </span>
                <span className="flex flex-row gap-2 items-center">
                  <MapPinIcon className="h-6 w-6" />
                  <Subheading>{ warehouse.address }</Subheading>
                </span>
              </div>
            </div> }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { warehouse && <div className="flex flex-col gap-4">
            <div className="grid grid-cols-4 items-center align-center" />
            <Divider />
            <Accordion title="Assets">
              <AssetTable
                filters={{ warehouseIdEq: warehouse.id }}
                hiddenColumns={['Warehouse']}
                pageSize={20}
                fixedHeight
              />
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { warehouse && <Button color="amber" onClick={onEdit}>Edit</Button> }
          { warehouse && <Button color="red" onClick={onDelete}>Delete</Button> }
        </DialogActions>
      </Dialog>
      { warehouse && <EditDialog id={warehouse.id} open={openEdit} setOpen={setOpenEdit} /> }
      {
        warehouse &&
          <DeleteDialog
            id={warehouse.id}
            mutation={DeleteMutation}
            open={openDelete}
            refetchQueries={[TableQuery]}
            setOpen={setOpenDelete}
            onSuccess={onDeleteSuccess}
          />
      }
    </>
  )
}
